// src/components/admin/VentasData/VentasData.jsx
import React, { useEffect, useMemo, useState } from 'react';
import './VentasData.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSync, faEye, faFileExcel, faFilePdf, faHandHoldingDollar, faTimes,
    faFilter, faTrash, faCheckCircle, faClock, faCoins, faPlus, faFilePdf as faPdf,
    faLayerGroup, faChevronDown, faChevronUp, faUser, faSearch, faInfoCircle,
    faPenToSquare
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moneda from '../../moneda';
import ventasService from '../../../services/ventasService';
import NewVenta from '../NewVenta/NewVenta';

export default function VentasData() {
    const [ventas, setVentas] = useState([]);
    const [todasVentas, setTodasVentas] = useState([]); // lista completa (para búsqueda inmune a filtros)
    const [estadisticas, setEstadisticas] = useState({});
    const [loading, setLoading] = useState(false);

    // Filtros
    const [filtroEstado, setFiltroEstado] = useState('');
    const [filtroDesde, setFiltroDesde] = useState('');
    const [filtroHasta, setFiltroHasta] = useState('');
    const [presetActivo, setPresetActivo] = useState('');
    const [busqueda, setBusqueda] = useState('');

    // Modal cliente (consolidado)
    const [modalVisible, setModalVisible] = useState(false);
    const [clienteActivoKey, setClienteActivoKey] = useState(null);

    // Detalle de una venta dentro del modal (abonos)
    const [ventaExpandida, setVentaExpandida] = useState(null);
    const [abonos, setAbonos] = useState([]);
    const [abonosLoading, setAbonosLoading] = useState(false);

    // Últimos abonos del cliente activo (todas sus ventas, más reciente primero)
    const [ultimosAbonos, setUltimosAbonos] = useState([]);
    const [ultimosAbonosLoading, setUltimosAbonosLoading] = useState(false);

    // Form abono general
    const [montoAbono, setMontoAbono] = useState('');
    const [metodoAbono, setMetodoAbono] = useState('Efectivo');
    const [notaAbono, setNotaAbono] = useState('');
    const [guardandoAbono, setGuardandoAbono] = useState(false);

    // Edición de una venta
    const [editVenta, setEditVenta] = useState(null);
    const [editForm, setEditForm] = useState({
        clienteNombre: '', clienteTelefono: '', elementoNombre: '', valorCompra: '', valorVenta: '',
    });
    const [guardandoEdicion, setGuardandoEdicion] = useState(false);

    // Toasts
    const [toasts, setToasts] = useState([]);
    const showToast = (message, type = 'info') => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3600);
    };

    useEffect(() => {
        cargarVentas();
    }, [filtroEstado, filtroDesde, filtroHasta]); // eslint-disable-line react-hooks/exhaustive-deps

    const cargarVentas = async () => {
        setLoading(true);
        const [data, todas] = await Promise.all([
            // limite alto: sin esto, el backend recorta a 100 filas por defecto y la
            // tabla de clientes (agrupada en el cliente) quedaba truncada — mostraba
            // menos clientes/ventas de los reales apenas se superaban los 100 registros,
            // aunque los KPIs de arriba (agregados en el servidor) sí eran correctos.
            ventasService.listar({ estado: filtroEstado, desde: filtroDesde, hasta: filtroHasta, limite: 1000 }),
            ventasService.listar({ limite: 1000 }), // lista completa para la búsqueda por persona
        ]);
        setLoading(false);
        if (data.error) {
            showToast(data.error, 'error');
            setVentas([]);
            setEstadisticas({});
        } else {
            setVentas(data.ventas || []);
            setEstadisticas(data.estadisticas || {});
        }
        if (!todas.error) setTodasVentas(todas.ventas || []);
    };

    const recargar = () => {
        cargarVentas();
        showToast('Datos actualizados', 'info');
    };

    // ---------- Helpers ----------
    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    const formatMoney = (v) =>
        `${moneda} ${Number(v || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    // Variante sin decimales para los KPIs de resumen (cobrado, por cobrar, ticket).
    const formatMoney0 = (v) =>
        `${moneda} ${Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 })}`;

    const gananciaDe = (v) => Number(v.valorVenta || 0) - Number(v.valorCompra || 0);
    const abonadoDe = (v) => v.totalAbonado ?? 0;
    const saldoDe = (v) => v.saldo ?? (Number(v.valorVenta || 0) - Number(v.totalAbonado || 0));

    const estadoBadge = (estado) => {
        const e = (estado || '').toLowerCase();
        if (e === 'pagado') return 'success';
        if (e === 'abonado') return 'warning';
        if (e === 'cancelado') return 'error';
        return '';
    };

    const estadoIcon = (estado) => {
        const e = (estado || '').toLowerCase();
        if (e === 'pagado') return faCheckCircle;
        if (e === 'abonado') return faCoins;
        return faClock;
    };

    const isCancelada = (v) => (v?.estado || '').toLowerCase() === 'cancelado';
    const isPagada = (v) => (v?.estado || '').toLowerCase() === 'pagado';
    // Venta "vigente": no cancelada y con saldo pendiente por cobrar.
    const esVigente = (v) => !isCancelada(v) && saldoDe(v) > 0.001;

    // ---------- Filtro local (búsqueda por texto) ----------
    // La búsqueda por persona es INMUNE a los filtros de estado/fecha: cuando hay
    // texto, se busca sobre la lista completa; sin texto, se respeta el filtro.
    const filtradas = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        if (!q) return ventas;
        return todasVentas.filter(v =>
            (v.clienteNombre || '').toLowerCase().includes(q) ||
            (v.elementoNombre || '').toLowerCase().includes(q) ||
            (v.clienteTelefono || '').toLowerCase().includes(q) ||
            (v.clienteIdentificacion || '').toLowerCase().includes(q)
        );
    }, [ventas, todasVentas, busqueda]);

    // ---------- Agrupación por cliente ----------
    // Identidad del cliente: idCliente (tabla `clientes`, fuente única). Si una venta
    // aún no lo trae (dato viejo antes de la migración), respaldo por teléfono
    // normalizado y, en su defecto, por nombre — igual que el resto del sistema.
    const soloDigitos = (t) => String(t || '').replace(/\D/g, '');
    const clienteKey = (v) => {
        if (v.idCliente) return `id:${v.idCliente}`;
        const tel = soloDigitos(v.clienteTelefono);
        if (tel) return `tel:${tel}`;
        return `nt:${(v.clienteNombre || '').trim().toLowerCase()}`;
    };

    // Orden FIFO: la venta más antigua primero (por fecha y, en empate, por id).
    const ordenFifo = (a, b) => {
        const fa = (a.createdAt || '');
        const fb = (b.createdAt || '');
        if (fa !== fb) return fa < fb ? -1 : 1;
        return (a.idVenta || 0) - (b.idVenta || 0);
    };

    const clientes = useMemo(() => {
        const map = new Map();
        for (const v of filtradas) {
            const key = clienteKey(v);
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    idCliente: v.idCliente || null,
                    clienteNombre: v.clienteNombre,
                    clienteTelefono: v.clienteTelefono,
                    clienteIdentificacion: v.clienteIdentificacion,
                    ventas: [],
                });
            }
            map.get(key).ventas.push(v);
        }

        const arr = Array.from(map.values()).map(c => {
            const ventasOrdenadas = [...c.ventas].sort(ordenFifo);              // antigua -> nueva
            const activas = ventasOrdenadas.filter(v => !isCancelada(v));       // histórico (incluye pagadas)
            const vigentes = activas.filter(esVigente);                        // con saldo por cobrar (FIFO)

            // Totales sobre ventas NO canceladas (mismo criterio que el backend/combo).
            const totalVenta = round2(activas.reduce((s, v) => s + Number(v.valorVenta || 0), 0));
            const totalAbonado = round2(activas.reduce((s, v) => s + abonadoDe(v), 0));
            const totalSaldo = round2(vigentes.reduce((s, v) => s + saldoDe(v), 0));
            const totalGanancia = round2(activas.reduce((s, v) => s + gananciaDe(v), 0));

            const base = activas.length ? activas : ventasOrdenadas;
            const articulos = base.map(v => v.elementoNombre).filter(Boolean);

            let estado;
            if (totalSaldo > 0.001) estado = totalAbonado > 0 ? 'Abonado' : 'Pendiente';
            else if (activas.length) estado = 'Pagado';
            else estado = 'Cancelado';

            // ultimaFecha = la venta más reciente (último elemento del orden FIFO ascendente)
            const ultimaFecha = ventasOrdenadas.length ? ventasOrdenadas[ventasOrdenadas.length - 1].createdAt : '';

            return {
                ...c,
                ventas: ventasOrdenadas,
                vigentes,
                totalVenta, totalAbonado, totalSaldo, totalGanancia,
                articulos,
                estado,
                ultimaFecha,
                count: ventasOrdenadas.length,
                activasCount: activas.length,
                vigentesCount: vigentes.length,
            };
        });

        // Prioriza a quien más debe.
        return arr.sort((a, b) => (b.totalSaldo - a.totalSaldo) || (b.count - a.count));
    }, [filtradas]); // eslint-disable-line react-hooks/exhaustive-deps

    const clienteActivo = useMemo(
        () => clientes.find(c => c.key === clienteActivoKey) || null,
        [clientes, clienteActivoKey]
    );

    // Vista previa de cómo se reparte el abono general (FIFO).
    const previewDistribucion = useMemo(() => {
        const monto = parseFloat(montoAbono);
        if (!clienteActivo || Number.isNaN(monto) || monto <= 0) return [];
        let restante = round2(monto);
        const res = [];
        for (const v of clienteActivo.vigentes) {
            if (restante <= 0.001) break;
            const saldoV = saldoDe(v);
            const aplicar = round2(Math.min(restante, saldoV));
            if (aplicar <= 0) continue;
            res.push({ idVenta: v.idVenta, elemento: v.elementoNombre, aplicar, saldoResultante: round2(saldoV - aplicar) });
            restante = round2(restante - aplicar);
        }
        return res;
    }, [montoAbono, clienteActivo]); // eslint-disable-line react-hooks/exhaustive-deps

    const limpiarFiltros = () => {
        setFiltroEstado('');
        setFiltroDesde('');
        setFiltroHasta('');
        setBusqueda('');
        setPresetActivo('');
    };

    // ---------- KPIs derivados del periodo ----------
    const totalVendido = Number(estadisticas.total_vendido || 0);
    const totalVentas = Number(estadisticas.total_ventas ?? filtradas.length) || 0;
    const gananciaTotal = Number(estadisticas.ganancia_total || 0);
    const cobradoPeriodo = Number(estadisticas.cobrado_periodo || 0);
    const ticketPromedio = totalVentas > 0 ? totalVendido / totalVentas : 0;
    const margenPct = totalVendido > 0 ? (gananciaTotal / totalVendido) * 100 : 0;

    // ---------- Comparativo vs. periodo anterior ----------
    // El backend solo envía 'comparativo' cuando hay rango de fechas completo.
    const comp = estadisticas.comparativo || null;
    const deltaPct = (actual, previo) => {
        const p = Number(previo || 0);
        if (p === 0) return actual > 0 ? 100 : null; // sin base previa comparable
        return ((Number(actual || 0) - p) / p) * 100;
    };

    // Renderiza la flecha ▲/▼ con el % de variación bajo un KPI.
    const renderDelta = (pct) => {
        if (comp === null || pct === null) return null;
        const up = pct >= 0;
        return (
            <span className={`kpi-delta ${up ? 'up' : 'down'}`}>
                {up ? '▲' : '▼'} {Math.abs(pct).toLocaleString('es-CO', { maximumFractionDigits: 1 })}%
            </span>
        );
    };

    // ---------- Rangos rápidos de fecha ----------
    const toISO = (d) => {
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
        return local.toISOString().slice(0, 10);
    };

    const aplicarPreset = (preset) => {
        if (preset === presetActivo) {
            // Toggle: si ya estaba activo, se limpia el rango
            setFiltroDesde('');
            setFiltroHasta('');
            setPresetActivo('');
            return;
        }
        const hoy = new Date();
        let desde = new Date(hoy);
        const hasta = new Date(hoy);
        if (preset === 'semana') {
            const dia = (hoy.getDay() + 6) % 7; // lunes = 0
            desde.setDate(hoy.getDate() - dia);
        } else if (preset === 'mes') {
            desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        } else if (preset === 'anio') {
            desde = new Date(hoy.getFullYear(), 0, 1);
        }
        setFiltroDesde(toISO(desde));
        setFiltroHasta(toISO(hasta));
        setPresetActivo(preset);
    };

    // ---------- Modal cliente ----------
    const cargarUltimosAbonos = async (idCliente) => {
        if (!idCliente) return setUltimosAbonos([]);
        setUltimosAbonosLoading(true);
        const data = await ventasService.ultimosAbonosCliente(idCliente, 3);
        setUltimosAbonosLoading(false);
        if (data.error) return setUltimosAbonos([]);
        setUltimosAbonos(data.abonos || []);
    };

    const abrirModal = (c) => {
        setClienteActivoKey(c.key);
        setModalVisible(true);
        setMontoAbono('');
        setNotaAbono('');
        setMetodoAbono('Efectivo');
        setVentaExpandida(null);
        setAbonos([]);
        setUltimosAbonos([]);
        cargarUltimosAbonos(c.idCliente);
    };

    const cerrarModal = () => {
        setModalVisible(false);
        setClienteActivoKey(null);
        setVentaExpandida(null);
        setAbonos([]);
        setMontoAbono('');
        setNotaAbono('');
        setUltimosAbonos([]);
    };

    const toggleVenta = async (v) => {
        if (ventaExpandida === v.idVenta) {
            setVentaExpandida(null);
            setAbonos([]);
            return;
        }
        setVentaExpandida(v.idVenta);
        setAbonosLoading(true);
        const data = await ventasService.listarAbonos(v.idVenta);
        setAbonosLoading(false);
        if (data.error) {
            showToast(data.error, 'warning');
            setAbonos([]);
            return;
        }
        setAbonos(data.abonos || []);
    };

    // ---------- Abono general (FIFO: paga primero las ventas más antiguas) ----------
    const registrarAbonoGeneral = async () => {
        if (!clienteActivo) return;
        const monto = parseFloat(montoAbono);
        if (Number.isNaN(monto) || monto <= 0) return showToast('Ingrese un monto válido', 'warning');
        if (monto > clienteActivo.totalSaldo + 0.001) {
            return showToast('El abono supera el total adeudado', 'warning');
        }

        setGuardandoAbono(true);
        let restante = round2(monto);
        let aplicadas = 0;
        let totalAplicado = 0;
        for (const v of clienteActivo.vigentes) { // ya en orden FIFO (antigua -> nueva)
            if (restante <= 0.001) break;
            const aplicar = round2(Math.min(restante, saldoDe(v)));
            if (aplicar <= 0) continue;
            const data = await ventasService.crearAbono({
                idVenta: v.idVenta,
                monto: aplicar,
                metodo: metodoAbono,
                nota: notaAbono.trim() || 'Abono general',
            });
            if (data.error) {
                showToast(`No se pudo abonar la venta #${v.idVenta}: ${data.error}`, 'error');
                break;
            }
            aplicadas += 1;
            totalAplicado = round2(totalAplicado + aplicar);
            restante = round2(restante - aplicar);
        }
        setGuardandoAbono(false);

        if (aplicadas > 0) {
            showToast(`Abono de ${formatMoney(totalAplicado)} aplicado a ${aplicadas} venta(s)`, 'success');
            setMontoAbono('');
            setNotaAbono('');
            // Si había una venta expandida, refresca sus abonos.
            if (ventaExpandida) {
                const data = await ventasService.listarAbonos(ventaExpandida);
                if (!data.error) setAbonos(data.abonos || []);
            }
            if (clienteActivo?.idCliente) cargarUltimosAbonos(clienteActivo.idCliente);
            await cargarVentas();
        }
    };

    const eliminarAbono = async (idAbono, idVenta) => {
        const { isConfirmed } = await Swal.fire({
            title: '¿Eliminar abono?',
            text: 'Se recalculará el saldo de la venta.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'No',
        });
        if (!isConfirmed) return;
        const data = await ventasService.eliminarAbono(idAbono);
        if (data.error) return showToast(data.error, 'error');
        showToast(data.mensaje || 'Abono eliminado', 'success');
        const rel = await ventasService.listarAbonos(idVenta);
        if (!rel.error) setAbonos(rel.abonos || []);
        if (clienteActivo?.idCliente) cargarUltimosAbonos(clienteActivo.idCliente);
        cargarVentas();
    };

    const cambiarEstadoVenta = async (v, estado) => {
        const cfg = estado === 'Pagado'
            ? {
                title: '¿Marcar como pagada?',
                html: `La venta de <b>${v.elementoNombre || ''}</b> quedará saldada. No se podrán registrar más abonos.`,
                icon: 'question',
                confirmButtonColor: '#10b981',
                confirmButtonText: 'Sí, marcar pagada',
            }
            : {
                title: '¿Cancelar esta venta?',
                html: `La venta de <b>${v.elementoNombre || ''}</b> quedará cancelada. Podrás consultarla pero no gestionar abonos.`,
                icon: 'warning',
                confirmButtonColor: '#ef4444',
                confirmButtonText: 'Sí, cancelar',
            };
        const { isConfirmed } = await Swal.fire({
            ...cfg,
            showCancelButton: true,
            cancelButtonColor: '#6b7280',
            cancelButtonText: 'No',
        });
        if (!isConfirmed) return;
        const data = await ventasService.actualizar(v.idVenta, { estado });
        if (data.error) return showToast(data.error, 'error');
        showToast(data.mensaje || 'Venta actualizada', 'success');
        cargarVentas();
    };

    const eliminarVenta = async (v) => {
        const { isConfirmed } = await Swal.fire({
            title: '¿Eliminar venta?',
            text: `Se eliminará la venta de ${v.elementoNombre}. Si tiene abonos, se intentarán trasladar a otra prenda pendiente o abonada del mismo cliente.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'No',
        });
        if (!isConfirmed) return;

        let data = await ventasService.eliminar(v.idVenta);

        if (data.advertencia === 'abono_excedente') {
            const destinos = data.destinos || [];
            const listaDestinos = destinos
                .map(d => `${d.elementoNombre} (${formatMoney(d.monto)})`)
                .join(', ');
            const html = destinos.length
                ? `Se trasladarán <b>${formatMoney(data.montoReasignable)}</b> a: <b>${listaDestinos}</b>, pero quedan <b>${formatMoney(data.montoExcedente)}</b> ya cobrados sin ninguna otra prenda pendiente a la cual aplicarlos.<br><br>Ese excedente se perderá si continuás. ¿Eliminar de todas formas?`
                : `Este cliente no tiene otra prenda pendiente ni abonada. Se perderán <b>${formatMoney(data.montoExcedente)}</b> ya cobrados por esta venta.<br><br>¿Eliminar de todas formas?`;
            const confirm2 = await Swal.fire({
                title: 'Abono excedente sin prenda',
                html,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#ef4444',
                cancelButtonColor: '#6b7280',
                confirmButtonText: 'Sí, perder el excedente y eliminar',
                cancelButtonText: 'Cancelar',
            });
            if (!confirm2.isConfirmed) return;
            data = await ventasService.eliminar(v.idVenta, true);
        }

        if (data.error) return showToast(data.error, 'error');
        const destinosOk = data.destinos || [];
        const mensaje = destinosOk.length
            ? `Venta eliminada. Se trasladaron ${formatMoney(data.montoReasignado)} a: ${destinosOk.map(d => d.elementoNombre).join(', ')}.`
            : (data.mensaje || 'Venta eliminada');
        showToast(mensaje, 'success');
        if (ventaExpandida === v.idVenta) {
            setVentaExpandida(null);
            setAbonos([]);
        }
        cargarVentas();
    };

    // ---------- Edición de venta ----------
    const abrirEdicion = (v) => {
        setEditVenta(v);
        setEditForm({
            clienteNombre: v.clienteNombre || '',
            clienteTelefono: v.clienteTelefono || '',
            elementoNombre: v.elementoNombre || '',
            valorCompra: v.valorCompra ?? '',
            valorVenta: v.valorVenta ?? '',
        });
    };

    const cerrarEdicion = () => {
        setEditVenta(null);
        setGuardandoEdicion(false);
    };

    const guardarEdicion = async () => {
        if (!editVenta) return;
        const nombre = editForm.clienteNombre.trim();
        const tel = editForm.clienteTelefono.trim();
        const elem = editForm.elementoNombre.trim();
        const vc = parseFloat(editForm.valorCompra);
        const vv = parseFloat(editForm.valorVenta);

        if (Number.isNaN(vc) || vc < 0) return showToast('Ingrese un valor de compra válido', 'warning');
        if (Number.isNaN(vv) || vv <= 0) return showToast('Ingrese un valor de venta válido', 'warning');
        if (vv < vc) return showToast('El valor de venta no puede ser menor al de compra', 'warning');
        if (!nombre) return showToast('El nombre del cliente es obligatorio', 'warning');
        if (!elem) return showToast('Indique el elemento vendido', 'warning');

        // No permitir que el nuevo valor de venta quede por debajo de lo ya abonado.
        const abonado = abonadoDe(editVenta);
        if (vv < abonado - 0.001) {
            return showToast(`El valor de venta no puede ser menor a lo ya abonado (${formatMoney(abonado)})`, 'warning');
        }

        setGuardandoEdicion(true);
        const data = await ventasService.actualizar(editVenta.idVenta, {
            clienteNombre: nombre,
            clienteTelefono: tel,
            elementoNombre: elem,
            valorCompra: vc,
            valorVenta: vv,
        });
        setGuardandoEdicion(false);
        if (data.error) return showToast(data.error, 'error');
        showToast(data.mensaje || 'Venta actualizada', 'success');
        cerrarEdicion();
        // Cambiar el teléfono reagrupa al cliente: cerramos el detalle para evitar
        // apuntar a una clave que ya no existe.
        if (soloDigitos(tel) !== soloDigitos(editVenta.clienteTelefono)) cerrarModal();
        await cargarVentas();
    };

    // ---------- Exportaciones ----------
    const descargarExcel = () => {
        const data = filtradas.map(v => ({
            'ID': v.idVenta,
            'Cliente': v.clienteNombre,
            'Teléfono': v.clienteTelefono || '',
            'Identificación': v.clienteIdentificacion || '',
            'Elemento': v.elementoNombre,
            'Origen': v.idProducto ? 'Catálogo' : 'Libre',
            'Valor compra': Number(v.valorCompra || 0),
            'Valor venta': Number(v.valorVenta || 0),
            'Ganancia': gananciaDe(v),
            'Abonado': abonadoDe(v),
            'Saldo': saldoDe(v),
            'Estado': v.estado,
            'Fecha': v.createdAt,
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Ventas');
        XLSX.writeFile(wb, 'ventas.xlsx');
        showToast('Excel generado', 'success');
    };

    const descargarPDF = () => {
        const pdf = new jsPDF('landscape');
        pdf.text('Clientes y ventas vigentes', 10, 10);
        const columns = ['Cliente', 'Teléfono', 'Ventas', 'Total', 'Abonado', 'Adeudado', 'Estado'];
        const body = clientes.map(c => [
            c.clienteNombre,
            c.clienteTelefono || '',
            c.activasCount,
            formatMoney(c.totalVenta),
            formatMoney(c.totalAbonado),
            formatMoney(c.totalSaldo),
            c.estado,
        ]);
        pdf.autoTable({ head: [columns], body, styles: { fontSize: 8 }, headStyles: { fillColor: [53, 92, 125] } });
        pdf.save('clientes-ventas.pdf');
        showToast('PDF generado', 'success');
    };

    // Comprobante consolidado del cliente (todas sus ventas vigentes).
    const descargarComprobanteCliente = async (c) => {
        const pdf = new jsPDF('p', 'mm', 'a4');
        let y = 15;
        pdf.setFontSize(16); pdf.setFont('helvetica', 'bold');
        pdf.text('Estado de cuenta del cliente', 10, y); y += 10;
        pdf.setFontSize(10); pdf.setFont('helvetica', 'normal');

        const cabecera = [
            ['Cliente', `${c.clienteNombre || ''}`],
            ['Teléfono', `${c.clienteTelefono || ''}`],
            ['Compras', `${c.activasCount}`],
            ['Ventas vigentes', `${c.vigentesCount}`],
            ['Total vendido', formatMoney(c.totalVenta)],
            ['Total abonado', formatMoney(c.totalAbonado)],
            ['Total adeudado', formatMoney(c.totalSaldo)],
        ];
        pdf.autoTable({
            startY: y, head: [['Campo', 'Valor']], body: cabecera,
            styles: { fontSize: 9, cellPadding: 3 }, headStyles: { fillColor: [53, 92, 125] },
            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 }, 1: { cellWidth: 125 } },
        });
        y = pdf.lastAutoTable.finalY + 8;

        pdf.setFontSize(12); pdf.setFont('helvetica', 'bold');
        pdf.text('Detalle de ventas vigentes', 10, y); y += 2;
        pdf.autoTable({
            startY: y + 2,
            head: [['#', 'Elemento', 'Fecha', 'Venta', 'Abonado', 'Saldo', 'Estado']],
            body: c.vigentes.map((v, i) => [
                i + 1,
                v.elementoNombre,
                v.createdAt,
                formatMoney(v.valorVenta),
                formatMoney(abonadoDe(v)),
                formatMoney(saldoDe(v)),
                v.estado,
            ]),
            styles: { fontSize: 8, cellPadding: 2 }, headStyles: { fillColor: [53, 92, 125] },
        });

        pdf.save(`cuenta_${(c.clienteNombre || 'cliente').replace(/\s+/g, '_')}.pdf`);
        showToast('Comprobante generado', 'success');
    };

    // ---------- UI ----------
    return (
        <div>
            {/* Toasts */}
            {toasts.length > 0 && (
                <div className="product-toast-container">
                    {toasts.map(t => (
                        <div key={t.id} className={`product-toast ${t.type}`}>
                            <div className="toast-icon">
                                {t.type === 'success' && '✅'}
                                {t.type === 'error' && '❌'}
                                {t.type === 'warning' && '⚠️'}
                                {t.type === 'info' && 'ℹ️'}
                            </div>
                            <div className="toast-content">
                                <div className="toast-title">{t.message}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filtros discretos — sobre las métricas. La búsqueda por persona es
                inmune a estado/fecha (busca en toda la lista). */}
            <div className="ventas-filtros-bar">
                <div className="vf-buscar">
                    <FontAwesomeIcon icon={faSearch} />
                    <input
                        type="text"
                        placeholder="Buscar persona en toda la lista (cliente, elemento o teléfono)"
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                    />
                    {busqueda && (
                        <button type="button" className="vf-clear-x" onClick={() => setBusqueda('')} title="Limpiar búsqueda">
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    )}
                </div>

                <select className="vf-select" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)} title="Estado">
                    <option value="">Estado: todos</option>
                    <option value="Pendiente">Pendiente</option>
                    <option value="Abonado">Abonado</option>
                    <option value="Pagado">Pagado</option>
                    <option value="Cancelado">Cancelado</option>
                </select>

                <div className="vf-fechas">
                    <input type="date" value={filtroDesde} onChange={e => { setFiltroDesde(e.target.value); setPresetActivo(''); }} title="Desde" />
                    <span className="vf-fechas-sep">→</span>
                    <input type="date" value={filtroHasta} onChange={e => { setFiltroHasta(e.target.value); setPresetActivo(''); }} title="Hasta" />
                </div>

                <div className="vf-presets">
                    {[
                        ['hoy', 'Hoy'],
                        ['semana', 'Semana'],
                        ['mes', 'Mes'],
                        ['anio', 'Año'],
                    ].map(([key, label]) => (
                        <button
                            key={key}
                            type="button"
                            className={`ventas-preset-chip${presetActivo === key ? ' active' : ''}`}
                            onClick={() => aplicarPreset(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <button className="vf-limpiar" onClick={limpiarFiltros} title="Limpiar filtros">
                    <FontAwesomeIcon icon={faFilter} /> Limpiar
                </button>
            </div>

            {/* Header + stats */}
            <div className="global-component-header" style={{ padding: '12px 16px' }}>
                <div className="flex-between" style={{ width: '100%' }}>
                    <div className="flex-center" style={{ gap: '16px', flexWrap: 'wrap' }}>
                        <div className="global-component-title">
                            <FontAwesomeIcon icon={faHandHoldingDollar} />
                            Ventas Gestionadas
                        </div>
                        <div className="global-component-stats ventas-stats">
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{clientes.length}</span>
                                <span className="global-stat-label">Clientes</span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{estadisticas.total_ventas ?? filtradas.length}</span>
                                <span className="global-stat-label">Ventas {renderDelta(deltaPct(totalVentas, comp?.total_ventas))}</span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{formatMoney0(cobradoPeriodo)}</span>
                                <span className="global-stat-label">Cobrado {renderDelta(deltaPct(cobradoPeriodo, comp?.cobrado_periodo))}</span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{formatMoney0(estadisticas.saldo_por_cobrar)}</span>
                                <span className="global-stat-label">Por cobrar</span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{formatMoney0(estadisticas.ganancia_total)}</span>
                                <span className="global-stat-label">Ganancia {renderDelta(deltaPct(gananciaTotal, comp?.ganancia_total))}</span>
                            </div>
                            <div className="global-stat-card compact" title="Ticket promedio = total vendido ÷ número de ventas. Es el valor medio de cada venta en el periodo.">
                                <span className="global-stat-number">{formatMoney0(ticketPromedio)}</span>
                                <span className="global-stat-label">Ticket prom. <FontAwesomeIcon icon={faInfoCircle} style={{ opacity: .6, fontSize: '0.8em' }} /></span>
                            </div>
                            <div className="global-stat-card compact">
                                <span className="global-stat-number">{margenPct.toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%</span>
                                <span className="global-stat-label">Margen</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex-center" style={{ gap: 8 }}>
                        <button className="btn-product-primary compact flex-center" onClick={descargarExcel} title="Exportar Excel">
                            <FontAwesomeIcon icon={faFileExcel} />
                        </button>
                        <button className="btn-product-primary compact flex-center" onClick={descargarPDF} title="Exportar PDF">
                            <FontAwesomeIcon icon={faFilePdf} />
                        </button>
                        <button className="btn-product-secondary compact flex-center" onClick={recargar} title="Actualizar">
                            <FontAwesomeIcon icon={faSync} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Botón nueva venta */}
            <div className="new-product-container">
                <NewVenta onCreated={cargarVentas} />
            </div>

            {/* Tabla (desktop) — agrupada por cliente */}
            <div className="global-table-container desktop-only">
                {loading ? (
                    <div className="global-loading-state">
                        <FontAwesomeIcon icon={faSync} size="2x" spin />
                        <div>Cargando ventas...</div>
                    </div>
                ) : clientes.length > 0 ? (
                    <table className="global-table">
                        <thead>
                            <tr>
                                <th>Cliente</th>
                                <th>Artículos adquiridos</th>
                                <th>Ventas</th>
                                <th>Total</th>
                                <th>Abonado</th>
                                <th>Adeudado</th>
                                <th>Estado</th>
                                <th>Última</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {clientes.map((c, index) => {
                                const items = c.vigentes.length ? c.vigentes : c.ventas;
                                return (
                                    <tr key={c.key} style={{ animationDelay: `${index * 0.05}s` }}>
                                        <td>
                                            <div>
                                                <strong>{c.clienteNombre}</strong><br />
                                                <small className="text-muted">{c.clienteTelefono || 'Sin contacto'}</small>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="venta-articulos-chips">
                                                {items.slice(0, 3).map(v => (
                                                    <span className="venta-chip" key={v.idVenta} title={v.elementoNombre}>{v.elementoNombre}</span>
                                                ))}
                                                {items.length > 3 && (
                                                    <span className="venta-chip more">+{items.length - 3}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="venta-count-badge" title="Compras del cliente">
                                                <FontAwesomeIcon icon={faLayerGroup} /> {c.activasCount}
                                            </span>
                                        </td>
                                        <td style={{ fontWeight: 700 }}>{formatMoney(c.totalVenta)}</td>
                                        <td>{formatMoney(c.totalAbonado)}</td>
                                        <td style={{ fontWeight: 700, color: c.totalSaldo > 0 ? '#dc2626' : '#16a34a' }}>{formatMoney(c.totalSaldo)}</td>
                                        <td>
                                            <span className={`global-indicator ${estadoBadge(c.estado)}`}>
                                                <FontAwesomeIcon icon={estadoIcon(c.estado)} /> {c.estado || '—'}
                                            </span>
                                        </td>
                                        <td>{c.ultimaFecha}</td>
                                        <td>
                                            <div className="global-table-actions">
                                                <button className="global-btn-edit" title="Ver / Gestionar" onClick={() => abrirModal(c)}>
                                                    <FontAwesomeIcon icon={faEye} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <div className="global-empty-state">
                        <FontAwesomeIcon icon={faHandHoldingDollar} size="3x" />
                        <div className="global-empty-title">No hay ventas</div>
                        <div className="global-empty-description">Registra una nueva venta o ajusta los filtros.</div>
                    </div>
                )}
            </div>

            {/* Tarjetas (mobile) — agrupadas por cliente */}
            {!loading && (
                <div className="mobile-only card-list">
                    {clientes.length === 0 ? (
                        <div className="global-empty-state">
                            <FontAwesomeIcon icon={faHandHoldingDollar} size="3x" />
                            <div className="global-empty-title">No hay ventas</div>
                            <div className="global-empty-description">Registra una nueva venta o ajusta los filtros.</div>
                        </div>
                    ) : (
                        clientes.map((c) => {
                            const items = c.vigentes.length ? c.vigentes : c.ventas;
                            return (
                                <div className="product-card" key={c.key}>
                                    <div className="card-header">
                                        <div className="card-image-container">
                                            <div className="card-image-placeholder">
                                                <FontAwesomeIcon icon={faUser} />
                                            </div>
                                        </div>
                                        <div className="card-info">
                                            <div className="card-title">{c.clienteNombre}</div>
                                            <div>
                                                <span className={`global-indicator ${estadoBadge(c.estado)}`}>
                                                    <FontAwesomeIcon icon={estadoIcon(c.estado)} /> {c.estado}
                                                </span>
                                                <span className="venta-count-badge" style={{ marginLeft: 8 }}>
                                                    <FontAwesomeIcon icon={faLayerGroup} /> {c.activasCount}
                                                </span>
                                            </div>
                                            <div className="text-muted venta-articulos-mobile">
                                                {items.map(v => v.elementoNombre).join(', ')}
                                            </div>
                                            <div className="text-muted">Adeudado: <strong style={{ color: c.totalSaldo > 0 ? '#dc2626' : '#16a34a' }}>{formatMoney(c.totalSaldo)}</strong></div>
                                        </div>
                                    </div>
                                    <div className="card-footer">
                                        <div className="card-stock">
                                            <span className="stock-label">Total:</span>
                                            <span className="stock-value">{formatMoney(c.totalVenta)}</span>
                                        </div>
                                        <div className="card-actions">
                                            <button className="global-btn-edit" title="Ver / Gestionar" onClick={() => abrirModal(c)}>
                                                <FontAwesomeIcon icon={faEye} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* Modal cliente consolidado */}
            {modalVisible && clienteActivo && (
                <div className="product-modal-backdrop" style={{ height: '100svh' }}>
                    <div className="product-modal-container" style={{ maxWidth: 900, height: '100svh', maxHeight: '100svh', display: 'flex', flexDirection: 'column' }}>
                        <div className="product-modal-header">
                            <div className="product-modal-title">
                                <FontAwesomeIcon icon={faUser} /> {clienteActivo.clienteNombre}
                            </div>
                            <button className="product-close-button" onClick={cerrarModal}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="product-modal-body" style={{ flex: 1, maxHeight: 'calc(100svh - 160px)', overflowY: 'auto' }}>
                            {/* Resumen consolidado */}
                            <div className="product-section-card mb-24">
                                <div className="product-section-header">
                                    <div className="product-section-title">Resumen del cliente</div>
                                    <span className={`global-indicator ${estadoBadge(clienteActivo.estado)}`}>
                                        <FontAwesomeIcon icon={estadoIcon(clienteActivo.estado)} /> {clienteActivo.estado}
                                    </span>
                                </div>
                                <div className="product-section-content">
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                        {[
                                            ['Teléfono', clienteActivo.clienteTelefono || '—'],
                                            ['Compras', `${clienteActivo.activasCount}`],
                                            ['Ventas vigentes', `${clienteActivo.vigentesCount}`],
                                            ['Total vendido', formatMoney(clienteActivo.totalVenta)],
                                            ['Total abonado', formatMoney(clienteActivo.totalAbonado)],
                                            ['Total adeudado', formatMoney(clienteActivo.totalSaldo)],
                                        ].map(([label, value]) => (
                                            <div className="product-form-group" key={label}>
                                                <label className="product-form-label">{label}</label>
                                                <input className="product-form-input" value={value ?? ''} disabled
                                                    style={label === 'Total adeudado' ? { fontWeight: 'bold', color: clienteActivo.totalSaldo > 0 ? '#dc2626' : '#16a34a' } : {}} />
                                            </div>
                                        ))}
                                    </div>

                                    {/* Últimos abonos del cliente (todas sus ventas) */}
                                    <div className="cliente-ultimos-abonos" style={{ marginTop: 16 }}>
                                        <div className="text-muted" style={{ fontWeight: 600, marginBottom: 8 }}>Últimos abonos</div>
                                        {ultimosAbonosLoading ? (
                                            <div className="text-muted">Cargando...</div>
                                        ) : ultimosAbonos.length > 0 ? (
                                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                                {ultimosAbonos.map(a => (
                                                    <li key={a.idAbono} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 14, padding: '6px 10px', background: 'rgba(0,0,0,.03)', borderRadius: 6 }}>
                                                        <span><strong>{formatMoney(a.monto)}</strong> · {a.elementoNombre}</span>
                                                        <span className="text-muted">{a.metodo || '—'} · {a.createdAt}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="text-muted">Sin abonos registrados.</div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Abono general (FIFO) */}
                            {clienteActivo.totalSaldo > 0.001 && (
                                <div className="product-section-card mb-24">
                                    <div className="product-section-header">
                                        <div className="product-section-title"><FontAwesomeIcon icon={faCoins} /> Abono general</div>
                                    </div>
                                    <div className="product-section-content">
                                        <p className="text-muted" style={{ marginTop: 0 }}>
                                            El monto se aplica a las ventas <strong>más antiguas primero</strong>; el excedente pasa a la siguiente venta pendiente.
                                        </p>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, alignItems: 'end' }}>
                                            <div className="product-form-group">
                                                <label className="product-form-label">Monto</label>
                                                <input type="number" min="0" step="0.01" className="product-form-input" value={montoAbono} onChange={e => setMontoAbono(e.target.value)} placeholder={`Máx ${formatMoney(clienteActivo.totalSaldo)}`} />
                                            </div>
                                            <div className="product-form-group">
                                                <label className="product-form-label">Método</label>
                                                <select className="product-form-input" value={metodoAbono} onChange={e => setMetodoAbono(e.target.value)}>
                                                    <option>Efectivo</option>
                                                    <option>Transferencia</option>
                                                    <option>Tarjeta</option>
                                                    <option>Otro</option>
                                                </select>
                                            </div>
                                            <div className="product-form-group">
                                                <label className="product-form-label">Nota</label>
                                                <input className="product-form-input" value={notaAbono} onChange={e => setNotaAbono(e.target.value)} maxLength={200} />
                                            </div>
                                            <div className="product-form-group">
                                                <button className="btn-product-primary flex-center" style={{ gap: 6 }} onClick={registrarAbonoGeneral} disabled={guardandoAbono}>
                                                    <FontAwesomeIcon icon={faPlus} /> {guardandoAbono ? 'Aplicando...' : 'Aplicar abono'}
                                                </button>
                                            </div>
                                        </div>

                                        {previewDistribucion.length > 0 && (
                                            <div className="abono-preview">
                                                <div className="abono-preview-title">Distribución del abono</div>
                                                {previewDistribucion.map((p, i) => (
                                                    <div className="abono-preview-row" key={p.idVenta}>
                                                        <span className="abono-preview-order">{i + 1}</span>
                                                        <span className="abono-preview-elemento">{p.elemento}</span>
                                                        <span className="abono-preview-monto">+{formatMoney(p.aplicar)}</span>
                                                        <span className="abono-preview-saldo">
                                                            Queda: <strong style={{ color: p.saldoResultante > 0 ? '#dc2626' : '#16a34a' }}>{formatMoney(p.saldoResultante)}</strong>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Ventas del cliente (FIFO) */}
                            <div className="product-section-card">
                                <div className="product-section-header">
                                    <div className="product-section-title">Ventas ({clienteActivo.ventas.length})</div>
                                </div>
                                <div className="product-section-content">
                                    {clienteActivo.ventas.map((v, i) => {
                                        const expandida = ventaExpandida === v.idVenta;
                                        return (
                                            <div className={`venta-item ${esVigente(v) ? '' : 'saldada'}`} key={v.idVenta}>
                                                <div className="venta-item-head">
                                                    <div className="venta-item-order">{i + 1}</div>
                                                    <div className="venta-item-main">
                                                        <div className="venta-item-nombre">
                                                            {v.elementoNombre}
                                                            <span className={`global-indicator ${estadoBadge(v.estado)}`} style={{ marginLeft: 8 }}>
                                                                <FontAwesomeIcon icon={estadoIcon(v.estado)} /> {v.estado}
                                                            </span>
                                                        </div>
                                                        <div className="text-muted venta-item-meta">
                                                            {v.createdAt} · {v.idProducto ? 'Catálogo' : 'Libre'}
                                                        </div>
                                                    </div>
                                                    <div className="venta-item-cifras">
                                                        <div><span className="text-muted">Venta:</span> <strong>{formatMoney(v.valorVenta)}</strong></div>
                                                        <div><span className="text-muted">Abonado:</span> {formatMoney(abonadoDe(v))}</div>
                                                        <div><span className="text-muted">Saldo:</span> <strong style={{ color: saldoDe(v) > 0 ? '#dc2626' : '#16a34a' }}>{formatMoney(saldoDe(v))}</strong></div>
                                                    </div>
                                                    <div className="venta-item-acciones">
                                                        <button className="global-btn-edit" title="Ver abonos" onClick={() => toggleVenta(v)}>
                                                            <FontAwesomeIcon icon={expandida ? faChevronUp : faChevronDown} />
                                                        </button>
                                                        {!isCancelada(v) && !isPagada(v) && (
                                                            <button className="global-btn-edit" style={{ background: '#10b981' }} title="Marcar pagada" onClick={() => cambiarEstadoVenta(v, 'Pagado')}>
                                                                <FontAwesomeIcon icon={faCheckCircle} />
                                                            </button>
                                                        )}
                                                        {!isCancelada(v) && (
                                                            <button className="global-btn-edit" style={{ background: '#3b82f6' }} title="Editar venta" onClick={() => abrirEdicion(v)}>
                                                                <FontAwesomeIcon icon={faPenToSquare} />
                                                            </button>
                                                        )}
                                                        <button className="global-btn-delete" title="Eliminar venta" onClick={() => eliminarVenta(v)}>
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </div>
                                                </div>

                                                {expandida && (
                                                    <div className="venta-item-abonos">
                                                        {abonosLoading ? (
                                                            <div className="global-loading-state"><div className="loading-spinner"></div> Cargando abonos...</div>
                                                        ) : abonos.length > 0 ? (
                                                            <table className="global-table">
                                                                <thead>
                                                                    <tr><th>#</th><th>Monto</th><th>Método</th><th>Nota</th><th>Fecha</th><th></th></tr>
                                                                </thead>
                                                                <tbody>
                                                                    {abonos.map((a, idx) => (
                                                                        <tr key={a.idAbono}>
                                                                            <td>{idx + 1}</td>
                                                                            <td style={{ fontWeight: 700 }}>{formatMoney(a.monto)}</td>
                                                                            <td>{a.metodo || '—'}</td>
                                                                            <td>{a.nota || '—'}</td>
                                                                            <td>{a.createdAt}</td>
                                                                            <td>
                                                                                <button className="global-btn-delete" title="Eliminar abono" onClick={() => eliminarAbono(a.idAbono, v.idVenta)}>
                                                                                    <FontAwesomeIcon icon={faTrash} />
                                                                                </button>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        ) : (
                                                            <div className="text-muted" style={{ padding: '8px 4px' }}>Sin abonos registrados para esta venta.</div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Footer acciones */}
                        <div className="product-submit-section">
                            <button className="btn-product-secondary" onClick={() => descargarComprobanteCliente(clienteActivo)}>
                                <FontAwesomeIcon icon={faPdf} style={{ marginRight: 6 }} /> Estado de cuenta PDF
                            </button>
                            <button className="btn-product-primary" onClick={cerrarModal}>Cerrar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal editar venta */}
            {editVenta && (
                <div className="product-modal-backdrop" style={{ zIndex: 1200 }}>
                    <div className="product-modal-container" style={{ maxWidth: 560 }}>
                        <div className="product-modal-header">
                            <div className="product-modal-title">
                                <FontAwesomeIcon icon={faPenToSquare} /> Editar venta
                            </div>
                            <button className="product-close-button" onClick={cerrarEdicion}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <div className="product-modal-body">
                            {/* Valores (prioritarios) */}
                            <div className="product-section-card mb-24">
                                <div className="product-section-header">
                                    <div className="product-section-title"><FontAwesomeIcon icon={faCoins} /> Valores</div>
                                </div>
                                <div className="product-section-content">
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
                                        <div className="product-form-group">
                                            <label className="product-form-label">Valor de compra *</label>
                                            <input type="number" min="0" step="0.01" className="product-form-input" value={editForm.valorCompra}
                                                onChange={e => setEditForm(f => ({ ...f, valorCompra: e.target.value }))} />
                                        </div>
                                        <div className="product-form-group">
                                            <label className="product-form-label">Valor de venta *</label>
                                            <input type="number" min="0" step="0.01" className="product-form-input" value={editForm.valorVenta}
                                                onChange={e => setEditForm(f => ({ ...f, valorVenta: e.target.value }))} />
                                        </div>
                                    </div>

                                    {(() => {
                                        const c = parseFloat(editForm.valorCompra);
                                        const v = parseFloat(editForm.valorVenta);
                                        if (Number.isNaN(c) || Number.isNaN(v)) return null;
                                        const g = round2(v - c);
                                        const m = v > 0 ? (g / v) * 100 : 0;
                                        return (
                                            <div style={{ marginTop: 16, padding: 12, borderRadius: 8, background: g >= 0 ? 'rgba(16,185,129,.1)' : 'rgba(239,68,68,.1)', border: `1px solid ${g >= 0 ? '#10b981' : '#ef4444'}` }}>
                                                <span style={{ color: g >= 0 ? '#16a34a' : '#dc2626', fontWeight: 600, fontSize: 14 }}>
                                                    {g >= 0
                                                        ? <>Ganancia: {formatMoney(g)} · Margen: {m.toFixed(1)}%</>
                                                        : <>⚠️ El valor de venta es menor al de compra</>}
                                                </span>
                                            </div>
                                        );
                                    })()}
                                    <small className="text-muted" style={{ display: 'block', marginTop: 8 }}>
                                        Los abonos ya registrados no cambian; el saldo se recalcula con el nuevo valor de venta.
                                    </small>
                                </div>
                            </div>

                            {/* Elemento y cliente */}
                            <div className="product-section-card">
                                <div className="product-section-header">
                                    <div className="product-section-title"><FontAwesomeIcon icon={faUser} /> Elemento y cliente</div>
                                </div>
                                <div className="product-section-content">
                                    <div className="product-form-group" style={{ marginBottom: 16 }}>
                                        <label className="product-form-label">Elemento *</label>
                                        <input className="product-form-input" value={editForm.elementoNombre} maxLength={160}
                                            onChange={e => setEditForm(f => ({ ...f, elementoNombre: e.target.value }))} />
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
                                        <div className="product-form-group">
                                            <label className="product-form-label">Cliente *</label>
                                            <input className="product-form-input" value={editForm.clienteNombre} maxLength={120}
                                                onChange={e => setEditForm(f => ({ ...f, clienteNombre: e.target.value }))} />
                                        </div>
                                        <div className="product-form-group">
                                            <label className="product-form-label">Teléfono</label>
                                            <input className="product-form-input" value={editForm.clienteTelefono} maxLength={40} inputMode="tel"
                                                onChange={e => setEditForm(f => ({ ...f, clienteTelefono: e.target.value }))} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="product-submit-section">
                            <button className="btn-product-secondary" onClick={cerrarEdicion}>Cancelar</button>
                            <button className="btn-product-primary flex-center" style={{ gap: 6 }} onClick={guardarEdicion} disabled={guardandoEdicion}>
                                <FontAwesomeIcon icon={faCheckCircle} /> {guardandoEdicion ? 'Guardando...' : 'Guardar cambios'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
